import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { format, subDays } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { FlaskConical } from "lucide-react";

interface CreateTestFlowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  /** Property default checklist — applied quietly; no picker in this modal. */
  defaultChecklistId?: string | null;
}

/** Temporary dogfood helper: creates an unassigned Test clean for today (assign from Schedule). */
export const CreateTestFlowModal = ({
  open,
  onOpenChange,
  propertyId,
  defaultChecklistId,
}: CreateTestFlowModalProps) => {
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  const today = format(new Date(), "yyyy-MM-dd");
  const yesterday = format(subDays(new Date(), 1), "yyyy-MM-dd");

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const { data: reservation, error: reservationError } = await supabase
        .from("reservations")
        .insert({
          property_id: propertyId,
          start_date: yesterday,
          end_date: today,
          source: "Test",
          summary: "Test clean",
          is_test: true,
        })
        .select()
        .single();

      if (reservationError) throw reservationError;

      const { error: taskError } = await supabase.from("cleaning_tasks").insert({
        property_id: propertyId,
        reservation_id: reservation.id,
        checklist_id: defaultChecklistId || null,
        cleaner_id: null,
        assignee_user_id: null,
        assigned_type: "unassigned",
        assignment_type: "manual",
        assignment_reason: "Test clean",
        scheduled_date: today,
        status: "scheduled",
        is_test: true,
      });

      if (taskError) throw taskError;

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["reservations", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["cleaning-tasks", propertyId] }),
        queryClient.invalidateQueries({ queryKey: ["my-tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["upcoming-cleaning-tasks"] }),
      ]);

      toast({
        title: "Test clean ready",
        description: "It shows on Schedule — tap Assign to give it to anyone.",
      });

      onOpenChange(false);
    } catch (error) {
      console.error("Error creating test clean:", error);
      toast({
        title: "Couldn’t create test clean",
        description: error instanceof Error ? error.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-amber-600" />
            Create test clean
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          <p className="text-sm text-muted-foreground">
            Creates a Test clean for today on Schedule. Leave it unassigned and use Assign to give it to anyone.
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={() => void handleCreate()} disabled={isCreating}>
            {isCreating ? "Creating…" : "Create test clean"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
